<template>
  <div>
    <b-row class="mb-3">
      <b-col 
        cols="8" 
        class="pr-0"
      >
        <b-form-select
          v-model="selectedShadowGroup"
          :options="shadowGroups"
          text-field="name"
          value-field="id"
          @change="handleShadowGroupChange"
        >
          <template #first>
            <b-select-option value="">
              Create new group
            </b-select-option>
          </template>
        </b-form-select>
      </b-col>
      
      <b-col 
        cols="4"
        class="px-1"
      >
        <b-button
          v-b-tooltip.hover
          :title="!selectedPreset ? 'Save' : 'Update'"
          type="button"
          variant="outline-primary"
          @click="$bvModal.show('shadow-group-name-modal')"
        >
          <b-icon
            v-if="!selectedShadowGroup"
            icon="save-fill"
          />

          <b-icon
            v-else
            icon="pencil-fill"
          />
        </b-button>

        <b-button
          v-if="selectedShadowGroup"
          v-b-tooltip.hover
          title="Delete"
          type="button"
          variant="outline-danger"
          @click="deleteShadowGroup"
        >
          <b-icon icon="bucket-fill" />
        </b-button>
      </b-col>
      
      <ShadowGroupNameModal 
        :title="shadowGroupNameModal.title" 
        :value="shadowGroupNameModal.value"
        placeholder="Please enter a shadow group name"
        submit-btn-title="Continue"
        @submit="handleShadowGroupSubmit"
      />
    </b-row>
    
    <b-card no-body>
      <b-tabs
        v-model="tabIndex"
        card
      >
        <b-tab
          v-for="(shadow, key) in tabs"
          :key="'key' + key"
        >
          <template #title>
            {{ 'Shadow ' + (key + 1) }}
            <b-button
              class="float-right close-button"
              pill
              size="sm"
              variant="danger"
              @click="closeTab(key)"
            >
              X
            </b-button>
          </template>
          <b-form>
            <b-form-group>
              <template #label>
                Shadow Presets
              </template>
              <b-form-group>
                <b-form-checkbox
                  v-if="localShadows[tabIndex]"
                  v-model="localShadows[tabIndex].disabled"
                  @change="onMirrorShadowChange"
                >
                  Temporarily disable shadow
                </b-form-checkbox>
              </b-form-group>
              <b-row>
                <b-col 
                  cols="6" 
                  class="pr-1"
                >
                  <b-form-select
                    v-model="selectedPreset"
                    :options="shadowPresets"
                    text-field="name"
                    value-field="id"
                    @change="handleSelectPresetChange"
                  >
                    <template #first>
                      <b-select-option value="">
                        create new
                      </b-select-option>
                    </template>
                  </b-form-select>
                </b-col>
                
                <b-col 
                  cols="6"
                  class="p-0"
                >
                  <b-button
                    v-if="selectedPreset"
                    v-b-tooltip.hover
                    title="Apply"
                    type="button"
                    variant="primary"
                    @click="applyPreset"
                  >
                    <b-icon icon="check-lg" />
                  </b-button>
                  <b-button
                    v-b-tooltip.hover
                    :title="selectedPreset ? `Edit`: 'Save'"
                    type="button"
                    variant="outline-primary"
                    @click="savePresetOfMirrorShadow"
                  >
                    <b-icon
                      v-if="!selectedPreset"
                      icon="save-fill"
                    />
                    <b-icon
                      v-else
                      icon="pencil-fill"
                    />
                  </b-button>
                  <b-button
                    v-if="selectedPreset"
                    v-b-tooltip.hover
                    title="Delete"
                    type="button"
                    variant="outline-danger"
                    @click="deletePreset"
                  >
                    <b-icon icon="bucket-fill" />
                  </b-button>
                </b-col>
              </b-row>
            </b-form-group>
          </b-form>
          <b-form-group label="Fade">
            <b-input-group>
              <b-input
                v-model="mirrorFade"
                :max="100"
                :min="0"
                type="range"
                @input="onMirrorShadowChange"
              />
              <template #append>
                <b-input
                  v-model="mirrorFade"
                  :max="100"
                  :min="0"
                  type="number"
                  @change="onMirrorShadowChange"
                />
              </template>
            </b-input-group>
          </b-form-group>
          <b-form-group label="Opacity">
            <b-input-group>
              <b-input
                v-model="mirrorOpacity"
                :max="100"
                :min="0"
                type="range"
                @input="onMirrorShadowChange"
              />
              <template #append>
                <b-input
                  v-model="mirrorOpacity"
                  :max="100"
                  :min="0"
                  type="number"
                  @input="onMirrorShadowChange"
                />
              </template>
            </b-input-group>
          </b-form-group>
          <b-form-group label="OffsetY">
            <b-input-group>
              <b-input
                v-model="mirrorOffsetY"
                :max="500"
                :min="-500"
                type="range"
                @input="onMirrorShadowChange"
              />
              <template #append>
                <b-input
                  v-model="mirrorOffsetY"
                  :max="500"
                  :min="-500"
                  type="number"
                  @input="onMirrorShadowChange"
                />
              </template>
            </b-input-group>
          </b-form-group>
        </b-tab>

        <!-- New Tab Button (Using tabs-end slot) -->
        <template #tabs-end>
          <b-nav-item
            href="#"
            role="presentation"
            @click.prevent="newTab"
          >
            <b>+</b>
          </b-nav-item>
        </template>

        <!-- Render this if no tabs -->
        <template #empty>
          <div class="text-center text-muted">
            There are no open tabs<br>
            Open a new tab using the <b>+</b> button above.
          </div>
        </template>
      </b-tabs>
    </b-card>
  </div>
</template>

<script>

import {
  COLOR_TYPE,
  BEHIND_DISPLAY_MODE,
  LINEAR_GRADIENT_COLOR_TYPE,
  IN_FRONT_DISPLAY_MODE,
  LEFT_LINEAR_GRADIENT_DIRECTION,
  RIGHT_LINEAR_GRADIENT_DIRECTION,
  TOP_LINEAR_GRADIENT_DIRECTION,
  BOTTOM_LINEAR_GRADIENT_DIRECTION, RADIAL_GRADIENT_COLOR_TYPE, DEFAULT_COLOR_STOPS, DEFAULT_COLOR
} from "@frontend/constants/oval-shadows";
import shadowPresetApi from '@frontend/services/api/shadow-preset'
import shadowGroupApi from '@frontend/services/api/shadow-group'
import ShadowGroupNameModal
  from "@frontend/components/modules/layouts-page/EditorControl/components/ShadowGroupNameModal.vue";

export default {
  name: 'MirrorShadow',
  components: {ShadowGroupNameModal},
  props: {
    target: {
      type: Object,
      default: () => ({}),
    },
    presetData: {
      type: Object,
      default: () => ({}),
    }
  },
  data() {
    return {
      tabIndex: 0,
      tabs: [],
      tabCounter: 0,
      mirrorFade: 0,
      mirrorOpacity: 1,
      mirrorOffsetY: 0,
      mirrorOffsetX: 0,
      localShadows: [],
      selectedLinearDirection: BOTTOM_LINEAR_GRADIENT_DIRECTION,
      selectedPreset: '',
      shadowPresets: [],
      shadowGroups: [],
      selectedShadowGroup: ''
    }
  },
  computed: {
    shadowGroupNameModal() {
      const selectedShadowGroup = this.shadowGroups.find(group => group.id === this.selectedShadowGroup);

      return {
        title: this.selectedShadowGroup ? 'Update Shadow Group' : 'Save Shadow Group',
        value: selectedShadowGroup ? selectedShadowGroup.name : ''
      }
    }
  },
  watch: {
    target: {
      immediate: true,
      handler(val) {
        const hasShadows = Array.isArray(val?.mirrorShadow) && val.mirrorShadow.length > 0;

        if (!hasShadows) {
          const defaultShadow = {
            fade: this.presetData.fade,
            opacity: this.presetData.opacity / 100,
            offsetY: this.presetData.offsetY,
            offsetX: this.presetData.offsetX,
            linearDirection: LEFT_LINEAR_GRADIENT_DIRECTION,
            disabled: false,
          };

          this.localShadows = [JSON.parse(JSON.stringify(defaultShadow))];
          this.tabs = [0];
          this.tabIndex = 0;
        } else {
          this.localShadows = JSON.parse(JSON.stringify(val.mirrorShadow));
          this.tabs = val.mirrorShadow.map((_, i) => i);
          this.tabIndex = 0;
        }

        this.selectedShadowGroup = val?.mirrorShadowGroupId || '';
        this.init();
      },
    },

    tabIndex() {
      this.initTab();
    },
  },
  mounted() {
    this.loadInitialData();
  },
  methods: {
    async loadInitialData() {
      const { data } = await shadowPresetApi.getAll({ type: 'MirrorShadow' });
      this.shadowPresets = data;
      await this.getShadowGroups();
    },
    newTab() {
      this.tabCounter += 1;
      this.tabs.push(this.tabCounter);

      const defaultShadow = {
        fade: this.presetData.fade,
        opacity: this.presetData.opacity / 100,
        offsetY: this.presetData.offsetY,
        offsetX: this.presetData.offsetX,
        linearDirection: LEFT_LINEAR_GRADIENT_DIRECTION,
        disabled: false,
      };

      const updated = [...(this.target.mirrorShadow || []), defaultShadow];
      this.target.set('mirrorShadow', updated);
      this.localShadows = JSON.parse(JSON.stringify(updated));

      if (typeof this.target.updateMirrorShadowData === 'function') {
        this.target.updateMirrorShadowData();
      }

      this.target.canvas.renderAll();

      setTimeout(() => {
        this.tabIndex = this.tabs.length - 1;
      }, 0);
    },
    initTab() {
      if (!this.target?.mirrorShadow?.[this.tabIndex]) return;
      this.setMirrorShadow(this.target.mirrorShadow[this.tabIndex]);
      this.selectedPreset = this.currentPreset(this.target.mirrorShadow[this.tabIndex])?.id || '';
    },
    onMirrorShadowChange() {
      if (!this.target || !this.localShadows[this.tabIndex] || !this.target.canvas) return;

      const shadow = this.localShadows[this.tabIndex];

      shadow.fade = this.mirrorFade;
      shadow.opacity = this.mirrorOpacity / 100;
      shadow.offsetY = this.mirrorOffsetY;
      shadow.offsetX = this.mirrorOffsetX;
      shadow.linearDirection = this.selectedLinearDirection;

      this.target.set('mirrorShadow', JSON.parse(JSON.stringify(this.localShadows)));

      if (typeof this.target.updateMirrorShadowData === 'function') {
        this.target.updateMirrorShadowData();
      }

      this.target.canvas.renderAll();
    },
    applyPreset() {
      const preset = this.shadowPresets.find(({ id }) => id === this.selectedPreset);
      this.setMirrorShadow(JSON.parse(preset.settings));
      this.onMirrorShadowChange();
      toastr.success("Shadow preset applied");
    },
    setMirrorShadow(shadowSettings) {
      const {
        fade,
        opacity,
        offsetY,
        offsetX,
        linearDirection,
      } = shadowSettings;
      if (!this.localShadows[this.tabIndex]) {
        this.localShadows[this.tabIndex] = {};
      }
      this.mirrorFade = fade;
      this.mirrorOpacity = opacity * 100;
      this.mirrorOffsetY = offsetY;
      this.mirrorOffsetX = offsetX;
      this.selectedLinearDirection = linearDirection;
    },
    init() {
      if (!this.target || this.target.type !== 'image') return;
      const hasSettings = Array.isArray(this.target.mirrorShadow) && this.target.mirrorShadow.length > 0;
      if (hasSettings) {
        this.tabs = this.target.mirrorShadow.map((_, key) => key);
        this.tabCounter = this.tabs.length - 1;
        this.localShadows = JSON.parse(JSON.stringify(this.target.mirrorShadow));
        this.tabIndex = this.tabs[0];
        this.initTab();
        return;
      }
      this.tabs.push(0);
      const { fade, opacity, offsetY, offsetX } = this.presetData;
      this.mirrorFade = fade;
      this.mirrorOpacity = opacity;
      this.mirrorOffsetY = offsetY;
      this.mirrorOffsetX = offsetX;
      this.localShadows = JSON.parse(JSON.stringify(this.target.mirrorShadow || []));
    },
    currentPreset(settings) {
      return this.shadowPresets.find(preset => preset.id === settings.presetId);
    },
    async savePresetOfMirrorShadow() {
      const selectedPreset = this.shadowPresets.find(preset => preset.id === this.selectedPreset);
      const swalData = await Swal.fire({
        title: selectedPreset ? 'Edit Mirror Shadow Preset' : `Save Mirror Shadow Preset`,
        html: `<div class="d-flex flex-column">
          <div>
            <input type="text" id="preset-name" class="form-control" placeholder="name" value="${selectedPreset ? selectedPreset.name : `Shadow ${this.tabIndex + 1}`}">
          </div>
          <div class="form-check ${!selectedPreset ? 'd-none' : ''}">
            <input type="checkbox" class="form-check-input" id="update-only-name">
            <label for="update-only-name" class="form-check-label">update name only</label>
          </div>
        </div>`,
        inputValue: selectedPreset ? selectedPreset.name : '',
        inputAttributes: {
          autocapitalize: "off"
        },
        showCancelButton: true,
        confirmButtonText: "Continue",
        showLoaderOnConfirm: true,
        preConfirm: () => {
          const name = document.getElementById('preset-name').value;
          const updateOnlyName = document.getElementById('update-only-name').checked;
          return { name, updateOnlyName };
        },
      });

      const { presetId, ...settings } = this.getCurrentShadowSettings();

      if (!swalData.isConfirmed) return;

      const data = {
        name: swalData.value.name,
        settings: JSON.stringify(settings),
        type: 'MirrorShadow'
      };

      if (selectedPreset) {
        if (swalData.value.updateOnlyName) {
          delete data.settings;
        }
        await shadowPresetApi.update(this.selectedPreset, data);

        Object.assign(this.shadowPresets.find(({ id }) => id == this.selectedPreset), data);
        toastr.success("Shadow preset updated");
      } else {
        const { data: preset } = await shadowPresetApi.create(data);
        this.shadowPresets.push(preset);
        this.selectedPreset = preset.id;
        toastr.success("Shadow preset saved");
        this.applyPreset();
      }
    },
    getCurrentShadowSettings() {
      return {
        presetId: this.selectedPreset || null,
        fade: this.mirrorFade,
        opacity: this.mirrorOpacity / 100,
        offsetY: this.mirrorOffsetY,
        offsetX: this.mirrorOffsetX,
        linearDirection: this.selectedLinearDirection || BOTTOM_LINEAR_GRADIENT_DIRECTION,
      };
    },
    deletePreset() {
      if (!this.selectedPreset) return;

      shadowPresetApi.delete(this.selectedPreset)
        .then(() => {
          this.shadowPresets = this.shadowPresets.filter(p => p.id !== this.selectedPreset);
          this.selectedPreset = '';
          toastr.success("Shadow preset deleted");
        })
        .catch(() => {
          toastr.error("Failed to delete shadow preset");
        });
    },
    handleShadowGroupSubmit(groupName) {
      if (this.selectedShadowGroup) {
        this.updateShadowGroup(groupName);
        return;
      }
      
      this.createShadowGroup(groupName);
    },
    async createShadowGroup(groupName) {
      try {
        const { data } = await shadowGroupApi.create({
          name: groupName,
          shadowSettings: this.target.mirrorShadow,
        });

        this.shadowGroups.push(data);
        this.selectedShadowGroup = data.id;
        this.target.set('mirrorShadowGroupId', data.id);

        toastr.success('The shadow group was created successfully.');
      } catch (error) {
        toastr.error('Something went wrong while creating the shadow group.');
      }
    },
    async updateShadowGroup(groupName) {
      try {
        const shadowGroup = this.shadowGroups.find(group => group.id === this.selectedShadowGroup);
        const targetSettings = [...this.target.mirrorShadow];
        
        const shadow_presets = targetSettings.map(setting => {
          const { presetId, ...settings } = setting;
          
          return {
            id: presetId || null,
            settings
          }
        });
        
        await shadowGroupApi.updateShadowGroup(shadowGroup.id, {
          name: groupName,
          shadow_presets
        });
        
        await this.getShadowGroups();

        toastr.success('The shadow group was updated successfully.');
      } catch (error) {
        toastr.error('Something went wrong while updating the shadow group.');
      }
    },
    async deleteShadowGroup() {
      try {
        await shadowGroupApi.deleteShadowGroup(this.selectedShadowGroup);

        this.shadowGroups.splice(this.shadowGroups.findIndex(group => group.id === this.selectedShadowGroup), 1);
        
        this.selectedShadowGroup = '';
        
        toastr.success('The shadow group was deleted successfully.');
      } catch (error) {
        toastr.error('Something went wrong while deleting the shadow group.');
      }
    },
    async getShadowGroups() {
      try {
        const { data } = await shadowGroupApi.getShadowGroups();

        this.shadowGroups = data;
      } catch (error) {
        console.error('An error occurred while getting shadow groups', error);
      }
    },
    handleShadowGroupChange(value) {
      if (!value) {
        this.target.set('mirrorShadow', null);
        this.target.set('mirrorShadowGroupId', null);

        if (typeof this.target.updateMirrorShadowData === 'function') {
          this.target.updateMirrorShadowData();
        }

        this.target.canvas.renderAll();
        this.tabs = [];
        this.selectedPreset = '';
        this.init();
        return;
      }
      
      const shadowGroup = this.shadowGroups.find(group => group.id === value);
      
      const shadowGroupPresets = shadowGroup.shadow_presets.map(preset => {
        return {
          ...JSON.parse(preset.settings),
          presetId: preset.id
        };
      });
      
      if (!shadowGroupPresets.length) {
        this.target.set('mirrorShadow', null);

        if (typeof this.target.updateMirrorShadowData === 'function') {
          this.target.updateMirrorShadowData();
        }

        this.target.canvas.renderAll();
        this.tabs = [];
        return;
      }

      this.target.set('mirrorShadow', [...shadowGroupPresets]);
      this.target.set('mirrorShadowGroupId', value);

      if (typeof this.target.updateMirrorShadowData === 'function') {
        this.target.updateMirrorShadowData();
      }

      this.target.canvas.renderAll();
      
      this.init();
    },
    handleSelectPresetChange() {
      if (!this.selectedPreset) {
        this.onMirrorShadowChange();
        return;
      }

      const preset = this.shadowPresets.find(p => p.id === this.selectedPreset);
      if (!preset) return;

      const settings = JSON.parse(preset.settings);
      
      this.setMirrorShadow(settings);
      this.onMirrorShadowChange();

      toastr.success("Preset applied");
    },
    onColorStopChange(event, key) {
      this.onMirrorShadowChange();
    },
    onColorOffsetChange(event, key) {
      this.onMirrorShadowChange();
    },
    closeTab(x) {
      this.tabs.splice(x, 1);
      const shadows = [...(this.target.mirrorShadow || [])];
      shadows.splice(x, 1);
      this.tabIndex = this.tabIndex - 1;
      this.target.set('mirrorShadow', shadows);
      this.target.canvas.renderAll();

      if (typeof this.target.updateMirrorShadowData === 'function') {
        this.target.updateMirrorShadowData();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.close-button {
  padding: 0 5px 0 5px;
  font-size: 12px;
  margin-left: 5px;
}

.color-stop-container {
  display: inline-flex;
  width: 100%;
  justify-content: center;
  align-content: center;
  align-items: center;
  .color-stop {
    width: 16%;
    height: 31px;
  }
  .color-stop-numbers {
    width: 30%;
  }
}
</style>
